import type { PersistentStorageKey } from '../../../storage';
import { persistentStorage } from '../../../storage';
import { showError } from '../../../toast';

/**
 * Снимок состояния видимости колонок, сохраняемый в `persistentStorage`.
 */
export type PersistedColumnVisibilityState = {
    /** Текущий выбор пользователя */
    visibleColumnIds: string[];
    /** Все колонки, с которыми пользователь уже сталкивался; используется,
     * чтобы отличить «новую колонку, которой ещё не было в релизе на момент сохранения»
     * от «колонки, которую пользователь явно скрыл», и не воскрешать второе */
    seenColumnIds: string[];
};

function readStringArray(value: unknown): string[] | null {
    if (!Array.isArray(value)) {
        return null;
    }

    return value.filter((item): item is string => typeof item === 'string');
}

/**
 * Читает через `persistentStorage` сохранённый снимок видимости колонок для ключа грида.
 *
 * @kind helper
 * @returns Снимок или `null`, если данных нет либо формат неверный.
 */
export function readPersistedColumnVisibilityState(
    storageKey: PersistentStorageKey
): PersistedColumnVisibilityState | null {
    const rawValue = persistentStorage.getItem(storageKey);

    if (rawValue === null) {
        return null;
    }

    let parsedValue: unknown;

    try {
        parsedValue = JSON.parse(rawValue);
    } catch {
        return null;
    }

    if (typeof parsedValue !== 'object' || parsedValue === null || Array.isArray(parsedValue)) {
        return null;
    }

    const visibleColumnIds = readStringArray((parsedValue as Record<string, unknown>).visibleColumnIds);
    const seenColumnIds = readStringArray((parsedValue as Record<string, unknown>).seenColumnIds);

    if (visibleColumnIds === null || seenColumnIds === null) {
        return null;
    }

    return { visibleColumnIds, seenColumnIds };
}

/**
 * Сохраняет через `persistentStorage` снимок видимости колонок для ключа грида.
 * @kind helper
 * @param storageKey - ключ грида
 * @param state - снимок видимости колонок
 * @returns void
 */
export function writePersistedColumnVisibilityState(
    storageKey: PersistentStorageKey,
    state: PersistedColumnVisibilityState
): void {
    try {
        persistentStorage.setItem(
            storageKey,
            JSON.stringify({
                visibleColumnIds: [...state.visibleColumnIds],
                seenColumnIds: [...state.seenColumnIds],
            })
        );
    } catch (error) {
        showError({ label: 'Не удалось сохранить настройки' }, error);
    }
}
